import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import * as z from "zod";
import logo from "@/assets/logo.svg";

import {
  Eye,
  EyeOff,
  Truck,
  Clock,
  Shield,
  Headphones,
  CheckCircle,
  Building2,
  Users,
  Globe,
  Package,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const signupSchema = z
  .object({
    // Company Information
    companyName: z.string().min(2, "Company name is required"),
    businessRegistration: z
      .string()
      .min(5, "Business registration number is required"),
    companyType: z.string().min(1, "Please select company type"),
    yearsInBusiness: z.string().min(1, "Please select years in business"),
    companySize: z.string().min(1, "Please select company size"),

    // Contact Information
    firstName: z.string().min(2, "First name is required"),
    lastName: z.string().min(2, "Last name is required"),
    jobTitle: z.string().optional(),
    businessEmail: z.string().email("Valid business email is required"),
    businessPhone: z.string().min(10, "Valid phone number is required"),
    website: z.string().url("Valid URL required").optional().or(z.literal("")),

    // Address Information
    addressLine1: z.string().min(5, "Address is required"),
    addressLine2: z.string().optional(),
    city: z.string().min(2, "City is required"),
    state: z.string().min(2, "State/Province is required"),
    zipCode: z.string().min(5, "ZIP/Postal code is required"),
    country: z.string().min(1, "Please select country"),

    // Shipping Information
    shippingVolume: z.string().min(1, "Please select shipping volume"),
    shipmentWeight: z.string().min(1, "Please select weight range"),
    shippingLanes: z.string().min(10, "Please describe your shipping lanes"),
    freightTypes: z
      .array(z.string())
      .min(1, "Please select at least one freight type"),

    // Account Setup
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
    termsAccepted: z
      .boolean()
      .refine(
        (val) => val === true,
        "You must accept the terms and conditions"
      ),
    marketingOptIn: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type SignupFormData = z.infer<typeof signupSchema>;

const freightTypeOptions = [
  "General Freight",
  "Refrigerated",
  "Hazmat",
  "Oversized",
  "Automotive",
  "Electronics",
  "Food & Beverage",
  "Pharmaceuticals",
];

export default function VendorSignup() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      freightTypes: [],
      termsAccepted: false,
      marketingOptIn: false,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = form;

  const watchedFields = watch();
  const totalFields = 23;
  const filledFields = Object.values(watchedFields).filter(
    (value) =>
      value !== "" &&
      value !== undefined &&
      value !== false &&
      (Array.isArray(value) ? value.length > 0 : true)
  ).length;
  const progress = Math.round((filledFields / totalFields) * 100);

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;

    if (strength <= 25) return { strength, label: "Weak", color: "bg-red-500" };
    if (strength <= 50)
      return { strength, label: "Fair", color: "bg-orange-500" };
    if (strength <= 75)
      return { strength, label: "Good", color: "bg-yellow-500" };
    return { strength, label: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(watchedFields.password || "");

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`;
  };

  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome Aboard!
            </h2>
            <p className="text-gray-600 mb-6">
              Your vendor account has been created successfully. Our team will
              review your application and contact you within 24 hours.
            </p>
            <Button asChild className="w-full">
              <Link to="/dashboard">Go to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 shadow-xl">
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="lg:pr-8">
            <div className="sticky top-8">
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <img
                    src={logo}
                    alt="Lucidity Freight Logo"
                    className="h-10 w-auto"
                  />
                  <span className="text-2xl font-bold text-gray-900">
                    Lucidity Freight
                  </span>
                </div>
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  Join our team of Shippers streamlining their freight
                  operations
                </h1>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <Clock className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-700">Real-time tracking</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-700">Competitive rates</span>
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-700">Reliable carriers</span>
                </div>
                <div className="flex items-center gap-3">
                  <Headphones className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-700">Helpful support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Signup Form */}
          <div>
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  Create Your Account
                </CardTitle>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Progress</span>
                    <span>{progress}% complete</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Company Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">
                        Company Information
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name *</Label>
                        <Input
                          id="companyName"
                          {...register("companyName")}
                          placeholder="Your Company Name"
                        />
                        {errors.companyName && (
                          <p className="text-sm text-red-600">
                            {errors.companyName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="businessRegistration">
                          Business Registration/EIN *
                        </Label>
                        <Input
                          id="businessRegistration"
                          {...register("businessRegistration")}
                          placeholder="123-45-6789"
                        />
                        {errors.businessRegistration && (
                          <p className="text-sm text-red-600">
                            {errors.businessRegistration.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companyType">Company Type *</Label>
                        <Select
                          onValueChange={(value) =>
                            setValue("companyType", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manufacturer">
                              Manufacturer
                            </SelectItem>
                            <SelectItem value="receiver">
                              Distributor
                            </SelectItem>
                            <SelectItem value="retailer">Retailer</SelectItem>
                            <SelectItem value="warehouse">Warehouse</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.companyType && (
                          <p className="text-sm text-red-600">
                            {errors.companyType.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="yearsInBusiness">
                          Years in Business *
                        </Label>
                        <Select
                          onValueChange={(value) =>
                            setValue("yearsInBusiness", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select years" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="<1">Less than 1 year</SelectItem>
                            <SelectItem value="1-5">1-5 years</SelectItem>
                            <SelectItem value="5-10">5-10 years</SelectItem>
                            <SelectItem value="10+">10+ years</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.yearsInBusiness && (
                          <p className="text-sm text-red-600">
                            {errors.yearsInBusiness.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="companySize">Company Size *</Label>
                        <Select
                          onValueChange={(value) =>
                            setValue("companySize", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-50">
                              11-50 employees
                            </SelectItem>
                            <SelectItem value="51-200">
                              51-200 employees
                            </SelectItem>
                            <SelectItem value="200+">200+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.companySize && (
                          <p className="text-sm text-red-600">
                            {errors.companySize.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Users className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">
                        Contact Information
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          {...register("firstName")}
                          placeholder="John"
                        />
                        {errors.firstName && (
                          <p className="text-sm text-red-600">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          {...register("lastName")}
                          placeholder="Smith"
                        />
                        {errors.lastName && (
                          <p className="text-sm text-red-600">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="jobTitle">Job Title</Label>
                        <Input
                          id="jobTitle"
                          {...register("jobTitle")}
                          placeholder="Logistics Manager"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="businessEmail">Business Email *</Label>
                        <Input
                          id="businessEmail"
                          type="email"
                          {...register("businessEmail")}
                          placeholder="john@company.com"
                        />
                        {errors.businessEmail && (
                          <p className="text-sm text-red-600">
                            {errors.businessEmail.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="businessPhone">Business Phone *</Label>
                        <Input
                          id="businessPhone"
                          {...register("businessPhone")}
                          placeholder="(555) 123-4567"
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            e.target.value = formatted;
                            setValue("businessPhone", formatted);
                          }}
                        />
                        {errors.businessPhone && (
                          <p className="text-sm text-red-600">
                            {errors.businessPhone.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="website">Website URL</Label>
                        <Input
                          id="website"
                          {...register("website")}
                          placeholder="https://www.company.com"
                        />
                        {errors.website && (
                          <p className="text-sm text-red-600">
                            {errors.website.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Globe className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">
                        Business Address
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="addressLine1">Address Line 1 *</Label>
                        <Input
                          id="addressLine1"
                          {...register("addressLine1")}
                          placeholder="123 Business Street"
                        />
                        {errors.addressLine1 && (
                          <p className="text-sm text-red-600">
                            {errors.addressLine1.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="addressLine2">Address Line 2</Label>
                        <Input
                          id="addressLine2"
                          {...register("addressLine2")}
                          placeholder="Suite 100"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="city">City *</Label>
                          <Input
                            id="city"
                            {...register("city")}
                            placeholder="New York"
                          />
                          {errors.city && (
                            <p className="text-sm text-red-600">
                              {errors.city.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="state">State/Province *</Label>
                          <Input
                            id="state"
                            {...register("state")}
                            placeholder="NY"
                          />
                          {errors.state && (
                            <p className="text-sm text-red-600">
                              {errors.state.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="zipCode">ZIP/Postal Code *</Label>
                          <Input
                            id="zipCode"
                            {...register("zipCode")}
                            placeholder="10001"
                          />
                          {errors.zipCode && (
                            <p className="text-sm text-red-600">
                              {errors.zipCode.message}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="country">Country *</Label>
                          <Select
                            onValueChange={(value) =>
                              setValue("country", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="us">United States</SelectItem>
                              <SelectItem value="ca">Canada</SelectItem>
                              <SelectItem value="mx">Mexico</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.country && (
                            <p className="text-sm text-red-600">
                              {errors.country.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Package className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">
                        Shipping Information
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shippingVolume">
                          Monthly Shipping Volume *
                        </Label>
                        <Select
                          onValueChange={(value) =>
                            setValue("shippingVolume", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select volume" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="<10">
                              Less than 10 loads
                            </SelectItem>
                            <SelectItem value="10-50">10-50 loads</SelectItem>
                            <SelectItem value="50-100">50-100 loads</SelectItem>
                            <SelectItem value="100+">100+ loads</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.shippingVolume && (
                          <p className="text-sm text-red-600">
                            {errors.shippingVolume.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="shipmentWeight">
                          Typical Weight Range *
                        </Label>
                        <Select
                          onValueChange={(value) =>
                            setValue("shipmentWeight", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select weight" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="<1000">
                              Less than 1,000 lbs
                            </SelectItem>
                            <SelectItem value="1000-5000">
                              1,000-5,000 lbs
                            </SelectItem>
                            <SelectItem value="5000-10000">
                              5,000-10,000 lbs
                            </SelectItem>
                            <SelectItem value="10000+">10,000+ lbs</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.shipmentWeight && (
                          <p className="text-sm text-red-600">
                            {errors.shipmentWeight.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="shippingLanes">
                        Primary Shipping Lanes/Routes *
                      </Label>
                      <Textarea
                        id="shippingLanes"
                        {...register("shippingLanes")}
                        placeholder="Describe your typical shipping routes (e.g., West Coast to East Coast, Regional distribution within Texas, etc.)"
                        rows={3}
                      />
                      {errors.shippingLanes && (
                        <p className="text-sm text-red-600">
                          {errors.shippingLanes.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Freight Types * (Select all that apply)</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {freightTypeOptions.map((type) => (
                          <div
                            key={type}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={type}
                              onCheckedChange={(checked) => {
                                const currentTypes =
                                  getValues("freightTypes") || [];
                                if (checked) {
                                  setValue("freightTypes", [
                                    ...currentTypes,
                                    type,
                                  ]);
                                } else {
                                  setValue(
                                    "freightTypes",
                                    currentTypes.filter((t) => t !== type)
                                  );
                                }
                              }}
                            />
                            <Label htmlFor={type} className="text-sm">
                              {type}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {errors.freightTypes && (
                        <p className="text-sm text-red-600">
                          {errors.freightTypes.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Account Setup */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Shield className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">Account Setup</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password">Password *</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            {...register("password")}
                            placeholder="Create a strong password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        {watchedFields.password && (
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span>Password strength</span>
                              <span
                                className={
                                  passwordStrength.strength >= 75
                                    ? "text-green-600"
                                    : "text-gray-600"
                                }
                              >
                                {passwordStrength.label}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div
                                className={`h-1 rounded-full transition-all ${passwordStrength.color}`}
                                style={{
                                  width: `${passwordStrength.strength}%`,
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {errors.password && (
                          <p className="text-sm text-red-600">
                            {errors.password.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">
                          Confirm Password *
                        </Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            {...register("confirmPassword")}
                            placeholder="Confirm your password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        {errors.confirmPassword && (
                          <p className="text-sm text-red-600">
                            {errors.confirmPassword.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="termsAccepted"
                          {...register("termsAccepted")}
                        />
                        <Label htmlFor="termsAccepted" className="text-sm">
                          I agree to the{" "}
                          <Link
                            to="/terms"
                            className="text-blue-600 hover:underline"
                          >
                            Terms & Conditions
                          </Link>{" "}
                          and{" "}
                          <Link
                            to="/privacy"
                            className="text-blue-600 hover:underline"
                          >
                            Privacy Policy
                          </Link>{" "}
                          *
                        </Label>
                      </div>
                      {errors.termsAccepted && (
                        <p className="text-sm text-red-600">
                          {errors.termsAccepted.message}
                        </p>
                      )}

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="marketingOptIn"
                          {...register("marketingOptIn")}
                        />
                        <Label htmlFor="marketingOptIn" className="text-sm">
                          I would like to receive marketing emails about new
                          features and industry insights
                        </Label>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Creating Account..." : "Create Account"}
                  </Button>

                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      Already have an account?{" "}
                      <Link
                        to="/signin"
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Sign in
                      </Link>
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
